/**
 * ReportsModule — admin Operational Reports & Export (ODE-P07-T09).
 *
 * Renders the fixed REQ-048 report set (per-day recap, per-DO rollup,
 * per-driver/vehicle rollup) from the guarded server read model — every number,
 * the aggregate totals, the deterministic sort and the opaque next-page cursor
 * are taken from the server projection verbatim (the client never slices,
 * re-pages or re-derives a report).
 *
 * Export (REQ-088) is presented as a separate, clearly-gated action: it reuses
 * the OD-015 financial export permission (purpose-code bound) and stays
 * fail-closed while the owner export policy is unapproved — the denied state
 * says so explicitly instead of pretending the export succeeded.
 *
 * REQ-090 states rendered: pending (not yet submitted), loading, ready, empty,
 * unauthorized, blocked, error — plus the export-specific `not_authorized`
 * (REQ-088) and the distinct role/purpose denial.
 *
 * Traces to: ODE-PRD-REQ-048, ODE-PRD-REQ-077, ODE-PRD-REQ-088,
 *            ODE-PRD-REQ-090.
 */

import { useAuth } from '../../auth/AuthContext.js';
import type { ReportExportResult, ReportRow } from '../../api/client.js';
import { StateView } from '../../components/StateView.js';
import type { ModuleState } from '../state.js';
import {
  REPORT_EXPORT_FORMATS,
  REPORT_KIND_LABELS,
  REPORT_KINDS,
  type ReportExportState,
  type ReportsKind,
  type ReportsState,
  useReports,
  type UseReportsOptions,
} from './useReports.js';

export interface ReportsSeams {
  readonly fetch?: UseReportsOptions['fetch'];
  readonly fetchExport?: UseReportsOptions['fetchExport'];
}

/** Fixed column set per report kind (mirror of the server CSV columns). */
export const REPORT_COLUMNS: Readonly<Record<ReportsKind, readonly string[]>> = {
  recap: [
    'day',
    'doCount',
    'journeyCount',
    'distanceMeters',
    'stopPointCount',
    'checkpointsCompleted',
    'uangJalanDisbursedCount',
    'amountsByCurrency',
  ],
  do_rollup: [
    'deliveryOrderId',
    'doNumber',
    'doStatus',
    'checkpointsTotal',
    'checkpointsCompleted',
    'journeyCount',
    'distanceMeters',
    'stopPointCount',
    'uangJalanStatus',
    'uangJalanAmount',
    'uangJalanCurrency',
    'lastActivityAt',
  ],
  driver_vehicle_rollup: [
    'driverId',
    'vehicleId',
    'journeyCount',
    'deliveryOrderCount',
    'distanceMeters',
    'stopPointCount',
    'firstActivityAt',
    'lastActivityAt',
  ],
};

/** Plain-Indonesian column labels (fixed order). */
export const REPORT_COLUMN_LABELS: Readonly<Record<string, string>> = {
  day: 'Hari (UTC)',
  doCount: 'Jumlah DO',
  journeyCount: 'Jumlah perjalanan',
  distanceMeters: 'Jarak (m)',
  stopPointCount: 'Titik berhenti',
  checkpointsCompleted: 'Checkpoint selesai',
  uangJalanDisbursedCount: 'Uang Jalan dicairkan',
  amountsByCurrency: 'Nominal per mata uang',
  deliveryOrderId: 'ID Delivery Order',
  doNumber: 'Nomor DO',
  doStatus: 'Status DO (server)',
  checkpointsTotal: 'Total checkpoint',
  uangJalanStatus: 'Status Uang Jalan',
  uangJalanAmount: 'Nominal',
  uangJalanCurrency: 'Mata uang',
  lastActivityAt: 'Aktivitas terakhir',
  driverId: 'ID driver',
  vehicleId: 'ID kendaraan',
  deliveryOrderCount: 'Jumlah DO',
  firstActivityAt: 'Aktivitas pertama',
};

/** Renders one server value verbatim; `null` is an explicit "unknown". */
export function formatReportCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
      a < b ? -1 : a > b ? 1 : 0,
    );
    if (entries.length === 0) return '—';
    return entries.map(([key, amount]) => `${key}: ${String(amount)}`).join(', ');
  }
  return String(value);
}

function TotalsPanel({ state }: { readonly state: ReportsState }): JSX.Element | null {
  if (state.kind !== 'ready' && state.kind !== 'empty') return null;
  const { totals } = state.page;
  return (
    <dl className="reports-totals" data-testid="reports-totals">
      <div>
        <dt>DO</dt>
        <dd data-testid="totals-doCount">{totals.doCount}</dd>
      </div>
      <div>
        <dt>Perjalanan</dt>
        <dd data-testid="totals-journeyCount">{totals.journeyCount}</dd>
      </div>
      <div>
        <dt>Jarak (m)</dt>
        <dd data-testid="totals-distanceMeters">{totals.distanceMeters}</dd>
      </div>
      <div>
        <dt>Checkpoint selesai</dt>
        <dd data-testid="totals-checkpointsCompleted">{totals.checkpointsCompleted}</dd>
      </div>
      <div>
        <dt>Uang Jalan dicairkan</dt>
        <dd data-testid="totals-uangJalanDisbursedCount">{totals.uangJalanDisbursedCount}</dd>
      </div>
      <div>
        <dt>Nominal</dt>
        <dd data-testid="totals-amountsByCurrency">
          {formatReportCell(totals.amountsByCurrency)}
        </dd>
      </div>
    </dl>
  );
}

function ReportTable({
  kind,
  rows,
}: {
  readonly kind: ReportsKind;
  readonly rows: readonly ReportRow[];
}): JSX.Element {
  const columns = REPORT_COLUMNS[kind];
  return (
    <table className="reports-table" data-testid="reports-table">
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column} scope="col">
              {REPORT_COLUMN_LABELS[column] ?? column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index} data-testid={`report-row-${index}`}>
            {columns.map((column) => (
              <td key={column} data-testid={`report-cell-${index}-${column}`}>
                {formatReportCell(row[column])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Maps the read state to the shared REQ-090 StateView state. */
export function toModuleState(state: ReportsState): ModuleState {
  switch (state.kind) {
    case 'idle':
      return { kind: 'pending' };
    case 'loading':
      return { kind: 'loading' };
    case 'empty':
      return { kind: 'empty', payload: null };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: state.message };
    case 'error':
      return { kind: 'error', message: state.message };
    case 'ready':
      return { kind: 'pending' };
  }
}

function downloadExport(result: ReportExportResult, filename: string): void {
  if (result.body === null || typeof document === 'undefined') return;
  try {
    const blob = new Blob([result.body], { type: result.contentType ?? 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    if (typeof URL.revokeObjectURL === 'function') {
      const revoke = URL.revokeObjectURL.bind(URL);
      window.setTimeout(() => revoke(url), 0);
    }
  } catch {
    // Restricted environment (e.g. jsdom): the bytes are already on screen via
    // the row count / output hash — no fabricated download is announced.
  }
}

function ExportPanel({
  state,
  filename,
  onRetry,
}: {
  readonly state: ReportExportState;
  readonly filename: string;
  readonly onRetry: () => void;
}): JSX.Element | null {
  if (state.kind === 'idle') return null;
  if (state.kind === 'loading') {
    return (
      <p className="reports-export-status" role="status" data-testid="report-export-loading">
        Menyiapkan ekspor…
      </p>
    );
  }
  if (state.kind === 'ready') {
    const { result } = state;
    return (
      <div className="reports-export-ready" role="status" data-testid="report-export-ready">
        <p>
          Ekspor siap: {result.rowCount ?? '—'} baris · hash keluaran (REQ-076){' '}
          <code data-testid="report-export-hash">{result.outputHash ?? '—'}</code>
        </p>
        <button
          type="button"
          data-testid="report-export-download"
          onClick={() => downloadExport(result, filename)}
        >
          Unduh
        </button>
      </div>
    );
  }
  if (state.kind === 'not_authorized') {
    return (
      <section
        role="alert"
        className="state state-blocked"
        data-testid="report-export-not-authorized"
      >
        <h3>Ekspor belum diizinkan</h3>
        <p>{state.message}</p>
      </section>
    );
  }
  if (state.kind === 'blocked') {
    return (
      <section role="alert" className="state state-blocked" data-testid="report-export-blocked">
        <h3>Ekspor ditolak</h3>
        <p>{state.message}</p>
      </section>
    );
  }
  if (state.kind === 'unauthorized') {
    return <StateView state={{ kind: 'unauthorized' }} />;
  }
  return (
    <section role="alert" className="state state-error" data-testid="report-export-error">
      <h3>Ekspor gagal</h3>
      <p>{state.message}</p>
      <button type="button" onClick={onRetry}>
        Coba lagi
      </button>
    </section>
  );
}

export function ReportsModule({
  seams = {},
}: { readonly seams?: ReportsSeams } = {}): JSX.Element {
  const { session } = useAuth();
  const reports = useReports({
    token: session?.token ?? null,
    ...(seams.fetch === undefined ? {} : { fetch: seams.fetch }),
    ...(seams.fetchExport === undefined ? {} : { fetchExport: seams.fetchExport }),
  });
  const { state, errors } = reports;
  const invalid = Object.values(errors).some((value) => value !== undefined);
  const kind = reports.form.kind;

  return (
    <section aria-labelledby="reports-title" className="module">
      <h2 id="reports-title">Laporan Operasional</h2>
      <p className="module-description">
        Rekap operasional read-only (REQ-048): rekap harian, rollup per Delivery Order
        dan rollup per driver/kendaraan. Semua angka berasal dari model baca server;
        ekspor bersifat stream-only dan terkunci sampai kebijakan ekspor pemilik
        disetujui (REQ-088).
      </p>

      <form
        className="reports-form"
        data-testid="reports-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!invalid) reports.submit();
        }}
      >
        <label>
          Jenis laporan
          <select
            data-testid="reports-kind"
            value={kind}
            onChange={(event) => reports.setField('kind', event.target.value)}
          >
            {REPORT_KINDS.map((value) => (
              <option key={value} value={value}>
                {REPORT_KIND_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Dari (UTC)
          <input
            data-testid="reports-from"
            value={reports.form.from}
            placeholder="YYYY-MM-DD"
            onChange={(event) => reports.setField('from', event.target.value)}
          />
        </label>
        <label>
          Sampai (UTC)
          <input
            data-testid="reports-to"
            value={reports.form.to}
            placeholder="YYYY-MM-DD"
            onChange={(event) => reports.setField('to', event.target.value)}
          />
        </label>
        <label>
          ID driver (opsional)
          <input
            data-testid="reports-driver-id"
            value={reports.form.driverId}
            onChange={(event) => reports.setField('driverId', event.target.value)}
          />
        </label>
        <label>
          ID kendaraan (opsional)
          <input
            data-testid="reports-vehicle-id"
            value={reports.form.vehicleId}
            onChange={(event) => reports.setField('vehicleId', event.target.value)}
          />
        </label>
        <label>
          ID Delivery Order (opsional)
          <input
            data-testid="reports-do-id"
            value={reports.form.doId}
            onChange={(event) => reports.setField('doId', event.target.value)}
          />
        </label>
        <label>
          Batas baris
          <input
            data-testid="reports-limit"
            value={reports.form.limit}
            onChange={(event) => reports.setField('limit', event.target.value)}
          />
        </label>
        <button type="submit" data-testid="reports-submit" disabled={invalid}>
          Tampilkan laporan
        </button>
      </form>

      {invalid && (
        <ul className="reports-form-errors" role="alert" data-testid="reports-form-errors">
          {Object.entries(errors).map(([field, message]) => (
            <li key={field} data-testid={`reports-error-${field}`}>
              {message}
            </li>
          ))}
        </ul>
      )}

      {state.kind === 'ready' && (
        <div className="reports-result" data-testid="reports-result">
          <p className="reports-meta" data-testid="reports-meta">
            Jenis {REPORT_KIND_LABELS[kind]} · urut {state.page.sort} · rentang{' '}
            {state.page.range.from ?? 'awal'}–{state.page.range.to ?? 'akhir'}
          </p>
          <ReportTable kind={kind} rows={state.page.rows} />
          <TotalsPanel state={state} />
          {state.page.hasMore && (
            <button type="button" data-testid="reports-next" onClick={() => reports.nextPage()}>
              Berikutnya
            </button>
          )}
        </div>
      )}

      {state.kind === 'empty' && (
        <div className="reports-result" data-testid="reports-result">
          <StateView state={{ kind: 'empty', payload: null }} />
          <TotalsPanel state={state} />
        </div>
      )}

      {state.kind !== 'ready' && state.kind !== 'empty' && (
        <StateView state={toModuleState(state)} onRetry={() => reports.submit()} />
      )}

      <section className="reports-export" aria-labelledby="reports-export-title">
        <h3 id="reports-export-title">Ekspor (REQ-088)</h3>
        <p>
          Ekspor memakai izin OD-015 <code>reports.financial.export</code> (wajib kode
          tujuan). Selama kebijakan ekspor pemilik belum disetujui, server menjawab{' '}
          <code>403 EXPORT_NOT_AUTHORIZED</code> tanpa satu byte pun data laporan.
        </p>
        <div className="reports-export-controls">
          <label>
            Format
            <select
              data-testid="report-export-format"
              value={reports.format}
              onChange={(event) =>
                reports.setFormat(event.target.value as (typeof REPORT_EXPORT_FORMATS)[number])
              }
            >
              {REPORT_EXPORT_FORMATS.map((value) => (
                <option key={value} value={value}>
                  {value.toUpperCase()}
                </option>
              ))}
            </select>
          </label>
          <label>
            Kode tujuan
            <input
              data-testid="report-export-purpose"
              value={reports.purposeCode}
              onChange={(event) => reports.setPurposeCode(event.target.value)}
              placeholder="mis. OPS-REVIEW-2026"
            />
          </label>
          <button
            type="button"
            data-testid="report-export-run"
            disabled={invalid}
            onClick={() => reports.runExport()}
          >
            Ekspor
          </button>
        </div>
        <ExportPanel
          state={reports.exportState}
          filename={`${kind}-${reports.form.from || 'all'}-${reports.form.to || 'all'}.${reports.format}`}
          onRetry={() => reports.runExport()}
        />
      </section>
    </section>
  );
}